/**
 * Diğer › Bilgi bankası: the knowledge entries the catalog import wrote, drafts first. Each
 * card shows the product, the sources and the two answers; Sercan can edit a draft's text and
 * approve it with a short hold (only approved entries reach the Temsilci), or retire an
 * entry. Nothing is applied optimistically: the list is read again after the server answers.
 */

import { useEffect, useState } from 'react';
import {
  KB_STATUS_LABELS,
  type KbEntry,
  type KbStatus,
  PRODUCT_STATUS_LABELS,
  sourceLabel,
  sourceUrl,
} from '../api/kb.js';
import { useStore } from '../app/context.js';
import { type KbState, useKb } from '../app/kb.js';
import { useReducedMotion } from '../app/media.js';
import { formatRelativeClock, formatSecondsLeft } from '../lib/format.js';
import { EmptyState, Segmented } from '../screens/common.js';
import { POLICY_HOLD_MS, useHold } from './HoldButton.js';
import { Icon } from './icons.js';

type KbFilter = 'taslak' | 'onayli' | 'emekli' | 'tumu';

const FILTER_STATUS: Readonly<Record<Exclude<KbFilter, 'tumu'>, KbStatus[]>> = {
  taslak: ['draft', 'conflict'],
  onayli: ['approved'],
  emekli: ['retired'],
};

function KbStatusLine({ state, reload }: { state: KbState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Bilgi bankası yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

function ApproveHold({ entry, disabled, onApprove }: { entry: KbEntry; disabled: boolean; onApprove: () => void }) {
  const store = useStore();
  const reducedMotion = useReducedMotion();
  const { holding, remaining, handlers } = useHold({
    durationMs: POLICY_HOLD_MS,
    disabled,
    onComplete: onApprove,
    onEarlyRelease: () => store.showToast('Onaylamak için 1,5 sn basılı tut', { icon: 'info' }),
  });
  return (
    <button
      type="button"
      className={`btn btn--primary kb-approve${holding ? ' kb-approve--holding' : ''}`}
      disabled={disabled}
      aria-label={
        holding
          ? `Onayla: basılı tutuluyor, ${formatSecondsLeft(remaining)} kaldı`
          : `Onayla: 1,5 saniye basılı tut. ${entry.pattern} cevabı Temsilci’ye açılır`
      }
      data-reduced-motion={reducedMotion ? 'true' : undefined}
      {...handlers}
    >
      <Icon name="check" size={16} />
      <span>{holding ? 'Basılı tut…' : 'Onayla'}</span>
      <span className={holding ? 'mono kb-approve__hint' : 'key kb-approve__hint'}>
        {holding ? formatSecondsLeft(remaining) : '1,5 sn'}
      </span>
    </button>
  );
}

function KbCard({ entry, now, busy }: { entry: KbEntry; now: number; busy: boolean }) {
  const store = useStore();
  const { store: kbStore } = useKb();
  const [answerTr, setAnswerTr] = useState(entry.answerTr);
  const [answerEn, setAnswerEn] = useState(entry.answerEn);
  const editable = entry.status === 'draft' || entry.status === 'conflict';
  const changed = answerTr !== entry.answerTr || answerEn !== entry.answerEn;
  const product = entry.products[0] ?? null;
  const approve = async () => {
    const input = {
      ...(answerTr !== entry.answerTr ? { answerTr } : {}),
      ...(answerEn !== entry.answerEn ? { answerEn } : {}),
    };
    const error = await kbStore.approve(entry.id, input);
    if (error === null) store.showToast(changed ? 'Düzeltilerek onaylandı' : 'Onaylandı', { icon: 'check' });
    else store.showToast(`Onaylanamadı: ${error}`, { icon: 'alert' });
  };
  const retire = async () => {
    const error = await kbStore.retire(entry.id);
    if (error === null) store.showToast('Emekliye ayrıldı', { icon: 'info' });
    else store.showToast(`Ayrılamadı: ${error}`, { icon: 'alert' });
  };
  return (
    <li className={`kb-card kb-card--${entry.status}`} aria-label={entry.pattern}>
      <header className="kb-card__head">
        <div className="kb-card__title">
          <strong>{product?.title ?? entry.pattern}</strong>
          {product ? (
            <span className="kb-card__meta">
              <span className="mono">{product.sku}</span>
              {' · '}
              {PRODUCT_STATUS_LABELS[product.status] ?? product.status}
              {product.listingIds.length > 0 ? ` · ilan ${product.listingIds.join(', ')}` : ''}
            </span>
          ) : null}
        </div>
        <span className={`kb-status kb-status--${entry.status}`}>{KB_STATUS_LABELS[entry.status]}</span>
      </header>
      <p className="kb-card__pattern">{entry.pattern}</p>
      {entry.questionExamples.length > 0 ? (
        <p className="kb-card__questions">Örnek sorular: {entry.questionExamples.join(' · ')}</p>
      ) : null}
      <label className="kb-field">
        <span className="kb-field__label">Cevap (TR)</span>
        <textarea
          className="kb-field__text"
          value={answerTr}
          readOnly={!editable}
          rows={5}
          onChange={(event) => setAnswerTr(event.target.value)}
        />
      </label>
      <label className="kb-field">
        <span className="kb-field__label">Cevap (EN, müşteriye giden)</span>
        <textarea
          className="kb-field__text"
          value={answerEn}
          readOnly={!editable}
          rows={5}
          onChange={(event) => setAnswerEn(event.target.value)}
        />
      </label>
      {entry.sources.length > 0 ? (
        <ul className="kb-sources" aria-label="Kaynaklar">
          {entry.sources.map((source) => {
            const label = sourceLabel(source);
            const url = sourceUrl(source);
            return (
              <li key={`${label}|${url ?? ''}`} className="kb-source">
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {label}
                  </a>
                ) : (
                  label
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      <footer className="kb-card__foot">
        <span className="kb-card__time">
          {entry.status === 'approved' && entry.approvedAt !== null
            ? `Onay: ${formatRelativeClock(entry.approvedAt, now)}${entry.approvedBy ? ` · ${entry.approvedBy}` : ''}`
            : `Güncelleme: ${formatRelativeClock(entry.updatedAt, now)}`}
        </span>
        <span className="kb-card__actions">
          {editable ? <ApproveHold entry={entry} disabled={busy} onApprove={() => void approve()} /> : null}
          {entry.status !== 'retired' ? (
            <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void retire()}>
              Emekliye ayır
            </button>
          ) : null}
        </span>
      </footer>
    </li>
  );
}

export function KbPage({ now }: { now: number }) {
  const { store, state } = useKb();
  const [filter, setFilter] = useState<KbFilter>('taslak');
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  if (state.view === null) return <KbStatusLine state={state} reload={() => void store.reload()} />;
  const view = state.view;
  if (view.entries.length === 0) {
    return (
      <EmptyState
        title="Henüz bilgi girişi yok"
        text="Ürün kataloğu yüklenince taslaklar burada görünür; onaylananlar Temsilci’nin cevaplarına girer."
      />
    );
  }
  const options: { id: KbFilter; label: string; count: number | null }[] = [
    { id: 'taslak', label: 'Taslak', count: view.counts.draft + view.counts.conflict },
    { id: 'onayli', label: 'Onaylı', count: view.counts.approved },
    { id: 'emekli', label: 'Emekli', count: view.counts.retired },
    { id: 'tumu', label: 'Tümü', count: view.entries.length },
  ];
  const shown =
    filter === 'tumu' ? view.entries : view.entries.filter((entry) => FILTER_STATUS[filter].includes(entry.status));
  return (
    <section className="kb-page" aria-label="Bilgi bankası">
      {/* The section page says what this is in one line; the longer help opens here (audit 25 Oct 2026, F18). */}
      <details className="kb-page__help">
        <summary className="kb-page__help-summary">
          <Icon name="chevron-down" size={16} />
          Nasıl çalışır
        </summary>
        <p className="kb-page__lead">
          Ürün kartlarından üretilen cevaplar. Taslaklar Temsilci’ye kapalıdır; okuyup gerekirse düzelt, sonra onayla.
          Her cevap neyin dosyadan ölçüldüğünü ve neyin doğrulanmadığını söyler.
        </p>
      </details>
      <Segmented label="Durum" options={options} value={filter} onChange={setFilter} controls="kb-list" />
      {shown.length === 0 ? (
        <p className="kb-page__empty">Bu durumda giriş yok.</p>
      ) : (
        <ul id="kb-list" className="kb-list" aria-label="Bilgi girişleri">
          {shown.map((entry) => (
            <KbCard
              key={`${entry.id}-${entry.status}-${entry.updatedAt}`}
              entry={entry}
              now={now}
              busy={state.busy.includes(entry.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
